import { motion } from 'framer-motion';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Calendar, Compass, ShieldCheck, Heart, MapPin, Award } from 'lucide-react';
import ImageGallery from '@/components/ImageGallery';
import { useLanguage } from '@/contexts/LanguageContext';
import SEO from '@/components/SEO';

const SupplierVigilionePage = () => {
  const { language } = useLanguage();
  const timelineEvents = [
    {
      year: "1937",
      title: language === 'PL' ? "Początki winnicy" : "The beginnings of the vineyard",
      icon: <Calendar className="w-5 h-5 text-accent" />,
      text: language === 'PL' ? "Winiarnia została założona przez dziadka obecnego właściciela w sercu regionu Murgia. Siedziba mieści się w odrestaurowanej Masserii z XVI wieku – tradycyjnym, obronnym gospodarstwie rolnym." : "The winery was founded by the current owner's grandfather in the heart of the Murgia region. The headquarters is located in a restored 16th-century Masseria - a traditional fortified farm.",
      image: "/suppliers/vigilione/map-puglia.webp",
      imgAlt: "Mapa Puglia Włochy"
    },
    {
      year: language === 'PL' ? "Lata 50. XX w." : "1950s",
      title: language === 'PL' ? "Przełomowa decyzja" : "A breakthrough decision",
      icon: <Compass className="w-5 h-5 text-accent" />,
      text: language === 'PL' ? "Rodzina stanęła przed kluczowym dylematem: czy zaorać winnice na rzecz \"poważniejszego\" rolnictwa, czy kontynuować mało wtedy opłacalną produkcję wina. Wybrali winiarstwo, co zdefiniowało przyszłość rodu na kolejne dekady." : "The family faced a key dilemma: whether to plow the vineyards in favor of \"more serious\" agriculture or continue the then-unprofitable wine production. They chose winemaking, which defined the family's future for decades to come."
    },
    {
      year: language === 'PL' ? "Od 1985" : "Since 1985",
      title: language === 'PL' ? "Era Giovanniego Zullo" : "The era of Giovanni Zullo",
      icon: <Award className="w-5 h-5 text-accent" />,
      text: language === 'PL' ? "Obecny właściciel, Giovanni Zullo, przejął stery w 1985 roku, gdy winnica liczyła zaledwie 3 hektary. Pod jego dynamiczną wodzą posiadłość rozrosła się do około 160 hektarów." : "The current owner, Giovanni Zullo, took the helm in 1985 when the vineyard was only 3 hectares. Under his dynamic leadership, the estate has grown to about 160 hectares.",
      image: "/suppliers/vigilione/owners.webp",
      imgAlt: "Giovanni Zullo w winnicy"
    },
    {
      year: "1987",
      title: language === 'PL' ? "Pionierzy ekologii" : "Pioneers of ecology",
      icon: <ShieldCheck className="w-5 h-5 text-accent" />,
      text: language === 'PL' ? "Już w 1987 roku, na długo przed dzisiejszą modą na ekologię, winnica całkowicie przeszła na produkcję organiczną (vino biologico), stając się jednym z niekwestionowanych prekursorów tego nurtu w regionie Apulia." : "Already in 1987, long before today's fashion for ecology, the vineyard completely switched to organic production (vino biologico), becoming one of the unquestionable precursors of this trend in the Apulia region."
    },
    {
      year: language === 'PL' ? "Współczesność" : "Present",
      title: language === 'PL' ? "Duma regionu Apulia" : "The pride of the Apulia region",
      icon: <Heart className="w-5 h-5 text-accent" />,
      text: language === 'PL' ? "Dziś winnica słynie z wyjątkowej odmiany Primitivo (szczególnie z apelacji Gioia del Colle DOC) oraz lokalnych szczepów, takich jak Nero di Troia, Susumaniello czy białe Verdeca i Fiano." : "Today the vineyard is famous for its unique Primitivo variety (especially from the Gioia del Colle DOC appellation) and local varieties such as Nero di Troia, Susumaniello or white Verdeca and Fiano.",
      image: "/suppliers/vigilione/vineyard.webp",
      imgAlt: "Winnice Tenuta Viglione"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SEO 
        title={language === 'PL' ? "Tenuta Viglione - Organiczne Wina z Apulii | Włoska Piwnica" : "Tenuta Viglione - Organic Wines from Apulia | Włoska Piwnica"}
        description={language === 'PL' ? "Poznaj winnicę Tenuta Viglione, pioniera ekologicznej uprawy w Apulii. Spróbuj wybitnych win z odmiany Primitivo Gioia del Colle DOC." : "Discover Tenuta Viglione winery, a pioneer of organic farming in Apulia. Taste outstanding wines from the Primitivo Gioia del Colle DOC variety."}
        canonicalUrl="/dostawcy/tenuta-vigilione"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Winery",
          "name": "Tenuta Viglione",
          "url": "https://wloska-piwnica.pl/dostawcy/tenuta-vigilione",
          "logo": "https://wloska-piwnica.pl/suppliers/vigilione/logo.svg",
          "description": "Rodzinna winnica z Apulii założona w 1937 roku. Producent organicznych win Primitivo Gioia del Colle DOC.",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "Apulia",
            "addressCountry": "IT"
          }
        }}
      />
      <Header />
      
      <main className="flex-grow pt-36 md:pt-40 lg:pt-44 pb-20">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden bg-[#1a0f0f] text-primary-foreground border-b border-border/20">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-70" style={{ backgroundImage: `url('/suppliers/vigilione/vineyard.webp')` }} />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1a0f0f]/50 to-[#1a0f0f]" />
          
          <div className="container relative z-10 mx-auto px-4 max-w-5xl text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center mb-6"
            >
              <img 
                src="/suppliers/vigilione/logo-alt.webp" 
                alt="Tenuta Viglione Logo" 
                className="h-28 md:h-36 object-contain bg-white/5 backdrop-blur-md p-4 rounded-xl border border-white/10"
              />
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-3xl sm:text-4xl md:text-6xl font-display font-bold mb-4 tracking-wide"
            >
              {language === 'PL' ? "Wina organiczne - Tenuta Viglione" : "Organic wines - Tenuta Viglione"}
            </motion.h1>
            
            <motion.div 
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="h-0.5 w-32 bg-accent mx-auto rounded-full mb-6"
            />
            
            <motion.p 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-lg md:text-xl text-primary-foreground/80 font-serif max-w-2xl mx-auto italic"
            >
              {language === 'PL' ? "„Pasja, miłość do ziemi i tradycja przekazywana z pokolenia na pokolenie w sercu Murgii.”" : "\"Passion, love for the land and tradition passed from generation to generation in the heart of Murgia.\""}
            </motion.p>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="grid md:grid-cols-5 gap-8 items-center">
              <div className="md:col-span-3 space-y-6">
                <h2 className="text-2xl md:text-3xl font-display font-bold text-primary">{language === 'PL' ? "Dziedzictwo z Apulii" : "Heritage from Apulia"}</h2>
                <p className="text-lg leading-relaxed text-foreground/80 font-serif">
                  {language === 'PL' ? "Tenuta Viglione to niezwykła historia rodziny Zullo, która od blisko stulecia przekształca owoce apulijskiej ziemi w płynną poezję. Położona w malowniczym regionie Gioia del Colle, winnica łączy w sobie szacunek dla przeszłości z nowoczesnym, ekologicznym podejściem do uprawy." : "Tenuta Viglione is the extraordinary story of the Zullo family, which has been transforming the fruits of Apulian land into liquid poetry for almost a century. Located in the picturesque Gioia del Colle region, the vineyard combines respect for the past with a modern, ecological approach to cultivation."}
                </p>
                <div className="flex items-center gap-3 text-sm text-foreground/60 font-semibold uppercase tracking-wider">
                  <MapPin className="text-accent w-5 h-5" />
                  <span>Murgia, Gioia del Colle, Puglia, Włochy</span>
                </div>
              </div>
              <div className="md:col-span-2 flex justify-center">
                <img 
                  src="/suppliers/vigilione/map-puglia.webp" 
                  alt="Lokalizacja w Apulii" 
                  loading="lazy"
                  className="rounded-xl border border-border shadow-md max-h-56 object-contain bg-white p-2"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Timeline Story Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-center text-primary mb-8 sm:mb-16">
              {language === 'PL' ? "Nasza Historia krok po kroku" : "Our History step by step"}
            </h2>

            <div className="relative border-l border-border md:border-l-0 md:before:absolute md:before:left-1/2 md:before:top-0 md:before:h-full md:before:w-0.5 md:before:bg-border space-y-16">
              {timelineEvents.map((event, index) => {
                const isEven = index % 2 === 0;
                return (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className={`relative pl-8 md:pl-0 md:grid md:grid-cols-2 md:gap-16 items-center ${isEven ? 'md:text-right' : ''}`}
                  >
                    {/* Timeline Node */}
                    <div className="absolute left-0 top-1.5 md:left-1/2 md:-translate-x-1/2 md:top-1/2 md:-translate-y-1/2 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card shadow-md">
                      {event.icon}
                    </div>

                    {/* Timeline Content Block */}
                    <div className={`${isEven ? 'md:col-start-1' : 'md:col-start-2'} space-y-3`}>
                      <span className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-widest bg-accent/15 text-accent rounded-full">
                        {event.year}
                      </span>
                      <h3 className="text-2xl font-bold text-primary font-display">{event.title}</h3>
                      <p className="text-foreground/80 leading-relaxed font-serif text-lg">{event.text}</p>
                    </div>

                    {/* Visual Asset Block (for rows that have pictures) */}
                    {event.image ? (
                      <div className={`mt-4 md:mt-0 ${isEven ? 'md:col-start-2' : 'md:col-start-1 md:row-start-1'} flex justify-center`}>
                        <motion.img 
                          whileHover={{ scale: 1.03 }}
                          src={event.image} 
                          alt={event.imgAlt} 
                          className="rounded-xl shadow-lg border border-border max-h-60 object-cover w-full max-w-sm"
                        />
                      </div>
                    ) : (
                      /* Empty spacing if no photo */
                      <div className="hidden md:block" />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Quality Banner & Certificates */}
        <section className="bg-primary/5 py-16 border-t border-b border-primary/10">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-12">
              <Award className="w-12 h-12 text-accent mx-auto mb-4" />
              <h3 className="text-2xl md:text-3xl font-display font-bold text-primary">{language === 'PL' ? "Certyfikowana Produkcja Biologiczna" : "Certified Organic Production"}</h3>
              <div className="h-0.5 w-24 bg-accent mx-auto rounded-full mt-4" />
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-stretch">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex flex-col gap-6 p-6 bg-card rounded-2xl border border-border shadow-sm items-center justify-center"
              >
                <img 
                  src="/suppliers/vigilione/cert-banner-1.webp" 
                  alt="Tenuta Viglione - Wina Ekologiczne" 
                  loading="lazy"
                  className="max-w-full h-auto rounded-lg shadow-sm hover:scale-[1.02] transition-transform duration-300"
                />
                <img 
                  src="/suppliers/vigilione/cert-logo-sidel.webp" 
                  alt="Produkt Ekologiczny" 
                  loading="lazy"
                  className="max-w-full h-auto rounded-lg shadow-sm hover:scale-[1.02] transition-transform duration-300"
                />
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="flex flex-col gap-6 p-6 bg-card rounded-2xl border border-border shadow-sm items-center justify-center"
              >
                <img 
                  src="/suppliers/vigilione/cert-banner-2.webp" 
                  alt="Gwarancja Czystości i Jakości" 
                  loading="lazy"
                  className="max-w-full h-auto rounded-lg shadow-sm hover:scale-[1.02] transition-transform duration-300"
                />
                <img 
                  src="/suppliers/vigilione/cert-badges.webp" 
                  alt="Oznaczenia Geograficzne i Biologiczne" 
                  loading="lazy"
                  className="max-w-full h-auto rounded-lg shadow-sm hover:scale-[1.02] transition-transform duration-300"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Action Button */}
        <div className="container mx-auto px-4 max-w-4xl text-center pb-12 pt-8">
            <a href="https://tenutaviglione.com" target="_blank" rel="noopener noreferrer" className="inline-block bg-primary text-primary-foreground font-bold py-3 px-8 rounded-full hover:bg-primary/90 transition-colors shadow-lg">
                {language === 'PL' ? "Dowiedz się więcej" : "Learn more"}
            </a>
        </div>

        {/* Gallery Section */}
        <ImageGallery 
          images={[
            "/suppliers/vigilione/gallery/vig-1.webp",
            "/suppliers/vigilione/gallery/vig-2.webp",
            "/suppliers/vigilione/gallery/vig-3.webp",
            "/suppliers/vigilione/gallery/vig-4.webp",
            "/suppliers/vigilione/gallery/vig-5.webp"
          ]} 
        />
      </main>

      <Footer />
    </div>
  );
};

export default SupplierVigilionePage;
